import { HQ } from "./sportConfig";
import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

const STORAGE_KEY = `${HQ.storagePrefix}-favorite-teams`;
const FavoritesContext = createContext(null);

const readLocal = () => {
  try {
    const value = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || "[]");
    return Array.isArray(value) ? [...new Set(value.filter((id) => HQ.validFavorite(id)))].slice(0, 100) : [];
  } catch {
    return [];
  }
};

const request = async (method, ids) => {
  const response = await fetch(`${HQ.api}/sportsim/favorite-teams`, {
    method,
    credentials: "same-origin",
    headers: method === "PUT" ? { "Content-Type": "application/json" } : undefined,
    body: method === "PUT" ? JSON.stringify(HQ.favoriteBody(ids)) : undefined,
  });
  if (!response.ok) throw new Error("Kunde inte synka favoritlag");
  return response.json().then(HQ.normalizeFavorites);
};

export function FavoriteTeamsProvider({ children }) {
  const location = useLocation();
  const pageIdentity = location.pathname === "/match"
    ? `${location.pathname}:${new URLSearchParams(location.search).get("url") || ""}`
    : location.pathname;
  const [teamIds, setTeamIds] = useState(readLocal);
  const [ready, setReady] = useState(false);
  const [authenticated, setAuthenticated] = useState(false);
  const [notice, setNotice] = useState("");
  const saveQueue = useRef(Promise.resolve());
  const saveVersion = useRef(0);
  const pageIdentityRef = useRef(pageIdentity);
  pageIdentityRef.current = pageIdentity;

  useEffect(() => { setNotice(""); }, [pageIdentity]);

  useEffect(() => {
    if (!HQ.capabilities.favoritesSync) { setTeamIds(readLocal()); setAuthenticated(false); setReady(true); return undefined; }
    let active = true;
    setReady(false);
    saveQueue.current.catch(() => {}).then(() => request("GET")).then(async (data) => {
      if (!active) return;
      if (!data.authenticated) {
        setAuthenticated(false);
        setTeamIds(readLocal());
      } else {
        const local = readLocal();
        const merged = [...new Set([...HQ.favoriteIds(data), ...local])];
        if (local.length) {
          try {
            await request("PUT", merged);
            window.localStorage.removeItem(STORAGE_KEY);
          } catch {
            if (active) setNotice("Kunde inte synka just nu. Favoriterna är sparade på den här enheten.");
          }
        }
        if (!active) return;
        setAuthenticated(true);
        setTeamIds(merged);
      }
      setReady(true);
    }).catch(() => {
      if (!active) return;
      setAuthenticated(false);
      setTeamIds(readLocal());
      setNotice("Favoriter sparas på den här enheten just nu.");
      setReady(true);
    });
    return () => { active = false; };
  }, [location.pathname]);

  const toggleTeam = (id) => {
    if (!ready || !HQ.validFavorite(id)) return;
    const next = teamIds.includes(id) ? teamIds.filter((teamId) => teamId !== id) : [...teamIds, id];
    setTeamIds(next);
    setNotice(next.includes(id) ? "Lag sparat i Mina lag." : "Lag borttaget från Mina lag.");
    if (authenticated) {
      const version = ++saveVersion.current;
      const noticeOrigin = pageIdentity;
      saveQueue.current = saveQueue.current.catch(() => {}).then(() => request("PUT", next));
      saveQueue.current.then(() => {
        if (version === saveVersion.current) window.localStorage.removeItem(STORAGE_KEY);
      }).catch(() => {
        if (version === saveVersion.current) {
          try {
            window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
            if (pageIdentityRef.current === noticeOrigin) setNotice("Kunde inte synka just nu. Mina lag är sparade på den här enheten.");
          } catch {
            if (pageIdentityRef.current === noticeOrigin) setNotice("Kunde inte spara Mina lag. Försök igen.");
          }
        }
      });
    } else {
      try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
        setNotice(next.includes(id) ? "Lag sparat i Mina lag på den här enheten." : "Lag borttaget från Mina lag på den här enheten.");
      } catch {
        setTeamIds(teamIds);
        setNotice("Kunde inte spara Mina lag på den här enheten. Försök igen.");
      }
    }
  };

  return <FavoritesContext.Provider value={{ teamIds, toggleTeam, ready, authenticated, notice }}>{children}</FavoritesContext.Provider>;
}

export const useFavoriteTeams = () => useContext(FavoritesContext);
